import { memo, useMemo } from 'react';
import {
  Cat,
  Eye,
  Flame,
  Skull,
  Link,
  Scale,
  Circle,
  CircleDashed,
  Moon,
  MoonStar,
  Sparkles,
  Leaf,
  Scroll,
  Crown,
  EyeOff,
  PenTool,
  Bird,
  Cross,
  SquareCheck,
  BadgeCheck,
  Gavel,
  Sunrise,
  VenetianMask,
  Sword,
  type LucideIcon,
} from 'lucide-react';

/**
 * Glyph — the ceremony icon system. This is the SANCTIONED replacement for
 * emoji anywhere in the product (emoji are banned: they render inconsistently
 * across platforms and break the cinematic tone). Every ritual/faction symbol
 * goes through here.
 *
 * Each glyph resolves in two stages:
 *  1. A hand-authored SVG at `src/assets/glyphs/<name>.svg` (the art track drops
 *     these in later). Loaded eagerly as a raw string via import.meta.glob so it
 *     inlines with `currentColor` — the SVGs MUST use `fill="currentColor"` /
 *     `stroke="currentColor"` so `tone` tinting works.
 *  2. Until that file exists, the closest `lucide-react` icon (below) stands in,
 *     tinted the same way. So the app is fully styled today and upgrades to
 *     bespoke art with zero call-site changes.
 *
 * ── GLYPH NAME CONTRACT (24) ──────────────────────────────────────────────────
 * The asset track must ship exactly these filenames in src/assets/glyphs/:
 *   wolfHead, seerEye, candle, skull, noose, scales,
 *   moonNew, moonCrescent, moonHalf, moonGibbous, moonFull,
 *   laurel, scroll, topHat, closedEye, quill, raven, claw,
 *   ballot, seal, gallows, dawnBird, mask, dagger
 * Adding/removing a name is a contract change — update this list, GLYPH_NAMES,
 * and the fallback map together.
 */

export type GlyphName =
  | 'wolfHead'
  | 'seerEye'
  | 'candle'
  | 'skull'
  | 'noose'
  | 'scales'
  | 'moonNew'
  | 'moonCrescent'
  | 'moonHalf'
  | 'moonGibbous'
  | 'moonFull'
  | 'laurel'
  | 'scroll'
  | 'topHat'
  | 'closedEye'
  | 'quill'
  | 'raven'
  | 'claw'
  | 'ballot'
  | 'seal'
  | 'gallows'
  | 'dawnBird'
  | 'mask'
  | 'dagger';

export type GlyphTone = 'gold' | 'blood' | 'bone' | 'arcana';

export interface GlyphProps {
  name: GlyphName;
  /** Pixel size (square). Default 24. */
  size?: number;
  /** Color family — maps to a token color. Default 'gold'. */
  tone?: GlyphTone;
  /** Extra classes on the wrapper. */
  className?: string;
  /** Accessible label. Omit → decorative (aria-hidden). */
  title?: string;
}

/** Ordered list of every glyph — handy for the CardLab kit + iteration. */
export const GLYPH_NAMES: GlyphName[] = [
  'wolfHead',
  'seerEye',
  'candle',
  'skull',
  'noose',
  'scales',
  'moonNew',
  'moonCrescent',
  'moonHalf',
  'moonGibbous',
  'moonFull',
  'laurel',
  'scroll',
  'topHat',
  'closedEye',
  'quill',
  'raven',
  'claw',
  'ballot',
  'seal',
  'gallows',
  'dawnBird',
  'mask',
  'dagger',
];

/** tone → CSS color (drives `currentColor`, so both SVG + lucide inherit it). */
const TONE_COLOR: Record<GlyphTone, string> = {
  gold: 'var(--gold-5, #e6b866)',
  blood: 'var(--blood-4, #990000)',
  bone: 'var(--state-dead, #9c8f86)',
  arcana: 'var(--arcana-3, #8b5cf6)',
};

/**
 * Closest lucide stand-in for each glyph until bespoke art lands. Chosen for
 * silhouette recognizability at small sizes, not literal accuracy.
 */
const FALLBACK: Record<GlyphName, LucideIcon> = {
  wolfHead: Cat, // closest quadruped head silhouette lucide ships
  seerEye: Eye,
  candle: Flame,
  skull: Skull,
  noose: Link, // a hanging loop
  scales: Scale,
  moonNew: Circle,
  moonCrescent: Moon,
  moonHalf: CircleDashed,
  moonGibbous: MoonStar,
  moonFull: Circle,
  laurel: Leaf,
  scroll: Scroll,
  topHat: Crown, // the Ringmaster's hat — regal headpiece stand-in
  closedEye: EyeOff,
  quill: PenTool,
  raven: Bird,
  claw: Sword, // slashing weapon silhouette
  ballot: SquareCheck,
  seal: BadgeCheck,
  gallows: Gavel, // judgement/execution instrument
  dawnBird: Sunrise,
  mask: VenetianMask,
  dagger: Cross, // blade cross-guard silhouette
};

// Eagerly inline any authored SVGs as raw strings. Keys look like
// '../assets/glyphs/skull.svg'. Empty today; auto-populates when files land.
const svgModules = import.meta.glob<string>('../assets/glyphs/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
});

function svgFor(name: GlyphName): string | undefined {
  const key = Object.keys(svgModules).find((k) => k.endsWith(`/${name}.svg`));
  return key ? svgModules[key] : undefined;
}

function GlyphImpl({ name, size = 24, tone = 'gold', className, title }: GlyphProps) {
  const color = TONE_COLOR[tone];
  const raw = useMemo(() => svgFor(name), [name]);

  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  const wrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    width: size,
    height: size,
    color,
    lineHeight: 0,
    flex: '0 0 auto',
  };

  if (raw) {
    // Inline the authored SVG; it inherits `color` via currentColor. We force
    // the outer <svg> to fill the box regardless of its intrinsic size.
    return (
      <span
        {...a11y}
        className={className}
        style={wrapperStyle}
        // The SVG source is a build-time asset from our own repo, not user input.
        dangerouslySetInnerHTML={{ __html: sizeSvg(raw, size) }}
      />
    );
  }

  const Icon = FALLBACK[name];
  return (
    <span {...a11y} className={className} style={wrapperStyle}>
      <Icon size={size} color="currentColor" strokeWidth={1.75} absoluteStrokeWidth />
    </span>
  );
}

/** Ensure an inlined SVG scales to the requested box and drops any fixed size. */
function sizeSvg(raw: string, size: number): string {
  return raw.replace(
    /<svg\b([^>]*)>/i,
    (_m, attrs: string) => {
      const cleaned = attrs
        .replace(/\swidth="[^"]*"/i, '')
        .replace(/\sheight="[^"]*"/i, '');
      return `<svg${cleaned} width="${size}" height="${size}" style="display:block">`;
    },
  );
}

export const Glyph = memo(GlyphImpl);
